import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';
import toast from 'react-hot-toast';

const statusLabels = {
  draft:     { label: 'Borrador',  color: 'badge-secondary' },
  sent:      { label: 'Enviado',   color: 'badge-info' },
  accepted:  { label: 'Aceptado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const BudgetList = () => {
  const [budgets, setBudgets] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', status: '', include_cancelled: false });
  const [cancelModal, setCancelModal] = useState(null); // budget a cancelar
  const [cancelling, setCancelling] = useState(false);

  const fetchBudgets = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.include_cancelled) params.include_cancelled = 1;
      const res = await api.get('/budgets', { params });
      setBudgets(res.data.data);
      setPagination(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchBudgets(1);
  }, [filters]);

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      await api.delete(`/budgets/${cancelModal.id}`);
      toast.success('Presupuesto cancelado');
      setCancelModal(null);
      fetchBudgets(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const formatAmount = (budget) => {
    // Usar totals calculados en backend, o calcular desde items si no hay
    const total = budget.totals?.grand_total ?? 0;
    if (budget.currency === 'usd') {
      const rate = Number(budget.usd_exchange_rate) || 1;
      return `$${(total / rate).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    }
    return `$${Number(total).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;
  };

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Presupuestos' }]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          Presupuestos
          {pagination.total > 0 && <span className="badge badge-secondary ml-2">{pagination.total}</span>}
        </h4>
        <Link to="/budgets/create" className="btn btn-primary btn-sm">
          <i className="ti-plus mr-1"></i> Nuevo Presupuesto
        </Link>
      </div>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row filter-row align-items-center">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <input type="text" className="form-control form-control-sm"
                placeholder="Buscar por folio o cliente..."
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })} />
            </div>
            <div className="col-6 col-md-3 mb-2 mb-md-0">
              <select className="form-control form-control-sm" value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Todos los estados</option>
                <option value="draft">Borrador</option>
                <option value="sent">Enviado</option>
                <option value="accepted">Aceptado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id="includeCancelled"
                  checked={filters.include_cancelled}
                  onChange={e => setFilters({ ...filters, include_cancelled: e.target.checked })} />
                <label className="custom-control-label" htmlFor="includeCancelled">
                  Ver cancelados
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="card-body p-0 mobile-card-list">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : budgets.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="ti-receipt" style={{ fontSize: '3rem' }}></i>
              <p className="mt-2">No hay presupuestos registrados</p>
            </div>
          ) : (
            <>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th className="mobile-hide">Tipo</th>
                    <th className="mobile-hide">Responsable</th>
                    <th>Total</th>
                    <th className="mobile-hide">Pagado</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map(budget => {
                    const totalPaid  = budget.totals?.total_paid  ?? 0;
                    const grandTotal = budget.totals?.grand_total ?? 0;
                    const balance    = grandTotal - totalPaid;
                    return (
                      <tr key={budget.id}
                        style={{ opacity: budget.status === 'cancelled' ? 0.6 : 1 }}>
                        <td className="td-name">
                          <span className="font-weight-bold">{budget.folio || `#${budget.id}`}</span>
                          <div className="d-md-none small text-muted mt-1">{budget.document_type?.name}</div>
                        </td>
                        <td>
                          {budget.case_file ? (
                            <div>
                              <Link to={`/clients/${budget.case_file.client_id}`} className="d-block font-weight-bold small">
                                {budget.case_file.client?.name}
                              </Link>
                              <small className="text-muted">{budget.case_file.title}</small>
                            </div>
                          ) : (
                            <span className="small">{budget.client_name || 'Público en general'}</span>
                          )}
                        </td>
                        <td className="mobile-hide">
                          <small>{budget.document_type?.name}</small>
                        </td>
                        <td className="mobile-hide">
                          <small>{budget.created_by?.name}</small>
                        </td>
                        <td data-label="Total">
                          <span className="font-weight-bold small">{formatAmount(budget)}</span>
                          {balance > 0 && grandTotal > 0 && (
                            <div className="small text-danger">
                              Saldo: ${Number(balance).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </td>
                        <td className="mobile-hide">
                          {totalPaid > 0 ? (
                            <span className="badge badge-success">
                              ${Number(totalPaid).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td data-label="Estado">
                          <span className={`badge ${statusLabels[budget.status]?.color}`}>
                            {statusLabels[budget.status]?.label}
                          </span>
                        </td>
                        <td className="td-actions">
                          <div className="d-flex" style={{ gap: '6px' }}>
                            <Link to={`/budgets/${budget.id}`} className="btn btn-sm btn-outline-primary">
                              <i className="ti-eye"></i>
                            </Link>
                            {budget.status === 'draft' && (
                              <button className="btn btn-sm btn-outline-danger"
                                onClick={() => setCancelModal(budget)}
                                title="Cancelar presupuesto">
                                <i className="ti-close"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center px-3 py-3 border-top flex-wrap" style={{ gap: '8px' }}>
                  <small className="text-muted">
                    Mostrando {pagination.from}–{pagination.to} de {pagination.total}
                  </small>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setCurrentPage(p => p - 1); fetchBudgets(currentPage - 1); }}>‹</button>
                    </li>
                    {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => { setCurrentPage(page); fetchBudgets(page); }}>{page}</button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => { setCurrentPage(p => p + 1); fetchBudgets(currentPage + 1); }}>›</button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal cancelación */}
      {cancelModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <svg style={{ width: 26, height: 26, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h5 style={{ marginBottom: '8px' }}>Cancelar Presupuesto</h5>
              <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: 0 }}>
                ¿Estás seguro que deseas cancelar el presupuesto <strong>{cancelModal.folio || `#${cancelModal.id}`}</strong>?
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="d-flex justify-content-end" style={{ gap: '12px' }}>
              <button className="btn btn-outline-secondary"
                onClick={() => setCancelModal(null)}
                disabled={cancelling}>
                Volver
              </button>
              <button className="btn btn-danger"
                onClick={handleCancel}
                disabled={cancelling}>
                {cancelling ? 'Cancelando...' : 'Sí, cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetList;